import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { getToken } from "../services/Token";
import {
  useLazyGetPayrollSettingsQuery,
  useLockPayrollMutation,
  useUnlockPayrollMutation,
  useReleasePayrollMutation,
} from "../api/payRollApi";

const EDIT_STATUS = { ON: "ON", OFF: "OFF" };
const RELEASE_STATUS = { HOLD: "HOLD", RELEASE: "RELEASE" };

const DIALOG = {
  NONE: "NONE",
  LOCK_CONFIRM: "LOCK_CONFIRM",
  UNLOCK_CONFIRM: "UNLOCK_CONFIRM",
  RELEASE_CONFIRM: "RELEASE_CONFIRM",
  NOT_LOCKED_WARNING: "NOT_LOCKED_WARNING",
};

function ControlRow({ title, description, action }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 3,
        px: 3,
      }}
    >
      <Box sx={{ maxWidth: "70%" }}>
        <Typography fontWeight={600} fontSize={15}>
          {title}
        </Typography>
        <Typography fontSize={13} color="text.secondary" mt={0.5}>
          {description}
        </Typography>
      </Box>
      <Box>{action}</Box>
    </Box>
  );
}

export default function PayrollSettings() {
  const tenantId = getToken("userId");
  const role = getToken("roles");
  const isAdmin = role ? role.includes("ADMIN") : false;

  const [selectedDate, setSelectedDate] = useState(null);
  const [dialog, setDialog] = useState(DIALOG.NONE);
  const [settings, setSettings] = useState(null);
  const [fetchSettings, { isFetching: fetchingSettings }] =
    useLazyGetPayrollSettingsQuery();
  const [lockPayroll, { isLoading: locking }] = useLockPayrollMutation();
  const [unlockPayroll, { isLoading: unlocking }] = useUnlockPayrollMutation();
  const [releasePayroll, { isLoading: releasing }] = useReleasePayrollMutation();

  const selectedMonth = selectedDate ? selectedDate.month() + 1 : null;
  const selectedYear = selectedDate ? selectedDate.year() : null;

  useEffect(() => {
    if (!selectedDate) return;
    fetchSettings({ tenantId, month: selectedMonth, year: selectedYear })
      .unwrap()
      .then((data) => setSettings(data))
      .catch((error) => {
        setSettings({
          payrollEditStatus: EDIT_STATUS.ON,
          payrollReleaseStatus: RELEASE_STATUS.HOLD,
        });
        toast.error( error.data ? error.data : "Error fetching payroll settings:");
      });
  }, [selectedDate]);

  const handleLockConfirm = async () => {
    setDialog(DIALOG.NONE);
    try {
      await lockPayroll({ tenantId, month: selectedMonth, year: selectedYear }).unwrap();
      toast.success(`Payroll locked for ${selectedDate.format("MMMM YYYY")}.`);
      setSettings((prev) => ({ ...prev, payrollEditStatus: EDIT_STATUS.OFF }));
    } catch (error) {
      toast.error(error?.data ? error.data : "Failed to lock payroll. Please try again.");
    }
  };

  const handleUnlockConfirm = async () => {
    setDialog(DIALOG.NONE);
    try {
      await unlockPayroll({ month: selectedMonth, year: selectedYear }).unwrap();
      toast.success(`Payroll unlocked for ${selectedDate.format("MMMM YYYY")}.`);
      setSettings((prev) => ({ ...prev, payrollEditStatus: EDIT_STATUS.ON }));
    } catch (err) {
      if (err?.status === 422) {
        const message =
          (typeof err?.data === "string" && err.data) ||
          err?.data?.message ||
          "Unable to unlock payroll.";
        toast.error(message);
      } else if (err?.status === 403) {
        toast.error("You do not have access to unlock payroll.");
      } else {
        toast.error("Failed to unlock payroll. Please try again.");
      }
    }
  };

  const handleReleaseConfirm = async () => {
    setDialog(DIALOG.NONE);
    try {
      await releasePayroll({ tenantId, month: selectedMonth, year: selectedYear }).unwrap();
      toast.success(`Payslips released for ${selectedDate.format("MMMM YYYY")}.`);
      setSettings((prev) => ({ ...prev, payrollReleaseStatus: RELEASE_STATUS.RELEASE }));
    } catch (err) {
      if (err?.status === 422) {
        setDialog(DIALOG.NOT_LOCKED_WARNING);
      } else if (err?.status === 409) {
        toast.error("Payslips for this month have already been released.");
      } else {
        toast.error("Failed to release payslips. Please try again.");
      }
    }
  };

  const isLocked = settings?.payrollEditStatus === EDIT_STATUS.OFF;
  const isReleased = settings?.payrollReleaseStatus === RELEASE_STATUS.RELEASE;
  const canUnlock = isAdmin && isLocked && !isReleased;

  const toggleSx = {
    textTransform: "none",
    fontWeight: 600,
    px: 3,
    "&.Mui-selected": { color: "#fff" },
    "&.Mui-disabled": { opacity: 0.5 },
  };

  const lockAction = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <ToggleButtonGroup
        value={isLocked ? "lock" : "unlock"}
        exclusive
        size="small"
      >
        <ToggleButton
          value="unlock"
          disabled={isLocked}
          sx={{
            ...toggleSx,
            "&.Mui-selected": { backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } },
          }}
        >
          Unlock
        </ToggleButton>
        <ToggleButton
          value="lock"
          disabled={isLocked || locking}
          onClick={() => !isLocked && setDialog(DIALOG.LOCK_CONFIRM)}
          sx={{
            ...toggleSx,
            "&.Mui-selected": { backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } },
          }}
        >
          {locking ? <CircularProgress size={14} sx={{ mr: 1 }} color="inherit" /> : null}
          Lock
        </ToggleButton>
      </ToggleButtonGroup>
      {/* {canUnlock && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={unlocking}
          onClick={() => setDialog(DIALOG.UNLOCK_CONFIRM)}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {unlocking ? <CircularProgress size={14} sx={{ mr: 1 }} color="inherit" /> : null}
          Unlock
        </Button>
      )} */}
    </Box>
  );

  const releaseAction = (
    <Tooltip title={!isLocked ? "Lock payroll first before releasing" : ""}>
      <span>
        <ToggleButtonGroup
          value={isReleased ? "release" : "hold"}
          exclusive
          size="small"
          disabled={!isLocked}
        >
          <ToggleButton
            value="release"
            disabled={isReleased || releasing}
            onClick={() => !isReleased && setDialog(DIALOG.RELEASE_CONFIRM)}
            sx={{
              ...toggleSx,
              "&.Mui-selected": { backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } },
            }}
          >
            {releasing ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
            Release
          </ToggleButton>
          <ToggleButton
            value="hold"
            disabled={!isReleased}
            sx={{
              ...toggleSx,
              "&.Mui-selected": { backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } },
            }}
          >
            Hold
          </ToggleButton>
        </ToggleButtonGroup>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", mt: 4, px: 2 }}>
      <Typography variant="h6" fontWeight={700} mb={3}>
        Payroll Settings
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="Select Payroll Month"
          views={["year", "month"]}
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          slotProps={{
            textField: { size: "small", sx: { width: 240 } },
          }}
        />
      </LocalizationProvider>

      {selectedDate && (
        <Box
          sx={{
            mt: 3,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 3, py: 2, backgroundColor: "#f5f5f5" }}>
            <Typography fontWeight={700} fontSize={15}>
              {selectedDate.format("MMMM YYYY")}
            </Typography>
          </Box>

          {fetchingSettings ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <ControlRow
                title="Payroll Lock"
                description="Lock the payroll to prevent further edits. This action is irreversible."
                action={lockAction}
              />
              <Divider />
              <ControlRow
                title="Employee View Release"
                description="Release payslips to employees. Payroll must be locked before releasing. Payslip emails will be sent to all employees."
                action={releaseAction}
              />
            </>
          )}
        </Box>
      )}

      {/* Lock Confirmation Dialog */}
      <Dialog open={dialog === DIALOG.LOCK_CONFIRM} onClose={() => setDialog(DIALOG.NONE)}>
        <DialogTitle>Lock Payroll for {selectedDate?.format("MMMM YYYY")}?</DialogTitle>
        <DialogContent>
          <Typography>
            Once locked, payroll cannot be edited and this action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(DIALOG.NONE)}>Cancel</Button>
          <Button variant="contained" onClick={handleLockConfirm}>
            Confirm Lock
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlock Confirmation Dialog */}
      <Dialog open={dialog === DIALOG.UNLOCK_CONFIRM} onClose={() => setDialog(DIALOG.NONE)}>
        <DialogTitle>Unlock Payroll for {selectedDate?.format("MMMM YYYY")}?</DialogTitle>
        <DialogContent>
          <Typography>
            This will allow payroll to be edited again for this month.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(DIALOG.NONE)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleUnlockConfirm}>
            Confirm Unlock
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Confirmation Dialog */}
      <Dialog open={dialog === DIALOG.RELEASE_CONFIRM} onClose={() => setDialog(DIALOG.NONE)}>
        <DialogTitle>Release Payslips for {selectedDate?.format("MMMM YYYY")}?</DialogTitle>
        <DialogContent>
          <Typography>
            Payslip emails will be sent to all employees for this month. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(DIALOG.NONE)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleReleaseConfirm}>
            Confirm Release
          </Button>
        </DialogActions>
      </Dialog>

      {/* Not Locked Warning Dialog */}
      <Dialog open={dialog === DIALOG.NOT_LOCKED_WARNING} onClose={() => setDialog(DIALOG.NONE)}>
        <DialogTitle>Payroll Not Locked</DialogTitle>
        <DialogContent>
          <Typography>
            Please lock the payroll before releasing the payslips.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDialog(DIALOG.NONE)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
